/**
 * @ottotester/permissions — single source of truth for role × action gates.
 *
 * One closed string union for roles, one nested matrix mapping
 * `(resource, action) → allowed roles`. Both server (route middleware)
 * and frontend (`useCanI` hook) import from here so UI affordances
 * and API enforcement can never drift.
 *
 * See docs/adrs/ for the rejected designs that shaped this:
 *   - 0001 — no per-resource ACLs
 *   - 0002 — no numeric / hierarchical role ordering
 *   - 0003 — no permissions in JWT
 *
 * Identity-aware constraints (e.g. "can't change your own role",
 * "last owner must transfer before leaving") are NOT encoded here —
 * matrix entries that have them carry a `// see handler` comment.
 * Keeps the matrix purely about (role, action), keeps predicates
 * audit-able by grep.
 */

/** Closed set of org-membership roles — GOVERNANCE TIER ONLY (#526 PR 3).
 *
 * Mirrors `OrgRole` in apps/server/src/db/models/Org.ts; not imported
 * from there to keep the package free of server-only deps. Drift is
 * caught by a compile-time type-equality assertion in the server's
 * middleware (`type _RolesMatch = Role extends OrgRole ? ... : false`) —
 * if the two unions diverge, `tsc` fails.
 *
 * Per ADR 0010, ottotester's access control splits into two tiers:
 *   - **Org tier** (this enum) — governance: billing, member
 *     management, workspace lifecycle. Answers "what can this user
 *     do at the org level."
 *   - **Workspace tier** (`WorkspaceRole` below) — operational:
 *     test artifacts, runs, workspace-member management. Answers
 *     "what can this subject do inside this workspace."
 *
 * Pre-#526 the four roles were `owner | admin | tester | reviewer`,
 * shared across both tiers. PR 3 reshapes the org tier to the
 * governance-only set:
 *
 *   owner   — full org control: billing, transfer ownership, delete
 *             org, plus implicit `admin` reach on every workspace in
 *             the org (audited per-access as
 *             `meta.tier = 'org-owner-implicit'`)
 *   admin   — manage members + workspace lifecycle + workspace-member
 *             management on any workspace, but NOT billing. NO
 *             implicit workspace operational access — must be
 *             explicitly granted into a workspace to actually run
 *             tests / edit plans there
 *   billing — payment, invoices, plan changes only. Cannot manage
 *             members or workspaces. Useful for finance personas
 *             who shouldn't have operational reach
 *   member  — the floor. Can be granted into workspaces and teams;
 *             can create workspaces (GitHub-style, per locked
 *             design decision); has no implicit org powers
 *
 * `tester` and `reviewer` move to the workspace tier as
 * `WorkspaceRole`. Existing dev DBs with org-tier `tester` /
 * `reviewer` get migrated to `member` by the boot-time
 * `migrateOrgRoleToGovernance` migration, with the equivalent
 * workspace grants pre-written by `backfillWorkspaceMemberships`.
 *
 * Two equivalent ways to refer to a role value:
 *   - String literal: `'owner'` — most concise; what's on the wire,
 *     in Mongo, in the matrix entries below.
 *   - Typed handle: `Role.owner` — autocomplete-friendly; useful in
 *     long expressions where the literal can be miskeyed.
 * Both resolve to the same string at runtime; pick whichever is
 * clearer at the call site. See ADR 0002 for why this is *not* a
 * TypeScript `enum` (no numeric ordering trap). */
export type Role = 'owner' | 'admin' | 'billing' | 'member'

/** Typed-handle accessor for role values. `Role.owner === 'owner'`
 * at runtime — pure renaming, no enum object semantics. */
export const Role = {
  owner: 'owner',
  admin: 'admin',
  billing: 'billing',
  member: 'member',
} as const satisfies Record<Role, Role>

/** All four org-tier roles — useful as the "any org member can do
 * this" value. */
export const ALL_ROLES: ReadonlyArray<Role> = [Role.owner, Role.admin, Role.billing, Role.member]

/** Org-tier permission matrix. Resources are top-level keys; each
 * resource maps action names to the list of roles allowed to perform
 * that action. Workspace-scoped resources (applications, variants,
 * testCases, ...) moved to `WORKSPACE_PERMISSIONS` in #526 PR 2 and
 * were removed from here in PR 3 — they live ONLY at the workspace
 * tier now.
 *
 * What's left here is purely governance: org-level metadata, members
 * + invitations, workspace lifecycle (the org-tier `create` action;
 * per-workspace `read/update/delete` moved to `WORKSPACE_PERMISSIONS`),
 * recovery flows, and billing. */
export const PERMISSIONS = {
  // ── Org metadata ──────────────────────────────────────────
  org: {
    read:              ALL_ROLES,
    update:            [Role.owner, Role.admin],
    delete:            [Role.owner],                        // destructive — owner only
    transferOwnership: [Role.owner],                        // tightened from owner+admin (Phase 8.5)
    readSettings:      [Role.owner, Role.admin],            // run budgets etc. — internal experimentation surface
    writeSettings:     [Role.owner, Role.admin],
  },
  members: {
    list:        ALL_ROLES,
    invite:      [Role.owner, Role.admin],
    changeRole:  [Role.owner, Role.admin],                  // see handler: notSelf, notLastOwner
    remove:      [Role.owner, Role.admin],                  // see handler: notSelf — must use members.leave
    leave:       ALL_ROLES,                                 // identity-based: own row only — see handler
  },
  invitations: {
    list:    [Role.owner, Role.admin],
    create:  [Role.owner, Role.admin],
    revoke:  [Role.owner, Role.admin],
  },

  // ── Teams (#526 PR 4) ─────────────────────────────────────
  // Teams are org-scoped groupings of users that can be granted
  // into workspaces (see ADR 0010). The lifecycle ops live at the
  // org tier — creating, renaming, deleting a team is a governance
  // action ("set up an organizational grouping"), not an
  // operational one. Granting a team INTO a workspace lives at the
  // workspace tier via `workspaceMembers.*` (the polymorphic-
  // subject endpoints in `routes/workspaces.ts`).
  teams: {
    list:   ALL_ROLES,                                      // anyone in the org can see the team list
    create: [Role.owner, Role.admin],
    update: [Role.owner, Role.admin],
    delete: [Role.owner, Role.admin],
  },
  teamMembers: {
    add:    [Role.owner, Role.admin],
    remove: [Role.owner, Role.admin],
  },

  // ── Workspace lifecycle (governance side only) ───────────
  // GitHub-style: any org Member can create a workspace and lands as
  // its workspace Admin (per locked decision §"Who can create
  // workspaces"). Per-workspace read/update/delete moved to
  // WORKSPACE_PERMISSIONS — those are operational, not governance.
  workspaces: {
    create: [Role.owner, Role.admin, Role.member],
  },

  // ── Recovery ──────────────────────────────────────────────
  recover: {
    list:             ALL_ROLES,                            // identity-scoped at handler
    recoverOrg:       [Role.owner],                         // see handler: must have been owner of deleted org
    recoverWorkspace: [Role.owner, Role.admin],
  },

  // ── Billing ──────────────────────────────────────────────
  // Dedicated namespace for the finance persona. `billing` can read
  // and edit billing-flow surfaces (payment, invoices, plan tier);
  // cannot touch members, workspaces, or org settings. Owner reaches
  // here implicitly (Owner has everything); admin can VIEW billing
  // info but not change it (finance authority belongs to billing or
  // owner). Reviewer/tester / org Member have no billing visibility.
  billing: {
    read:  [Role.owner, Role.admin, Role.billing],
    write: [Role.owner, Role.billing],                      // change payment method, plan, etc.
  },

  // ── Audit log (#1119) ─────────────────────────────────────
  // Read-only governance view of the org's own audit trail (who did
  // what, when). Owner + admin only — it surfaces member/credential/
  // integration activity across the org, so it's an admin-tier
  // governance surface, not something every member or the billing
  // persona sees. The rows are server-redacted (see application/audit).
  audit: {
    read: [Role.owner, Role.admin],
  },
} as const satisfies Record<string, Record<string, ReadonlyArray<Role>>>

/** Resource keys (top level of the matrix). */
export type Resource = keyof typeof PERMISSIONS

/** All `(resource, action)` action keys, dot-joined. Drives the typed
 * accessor `actionRoles()` and the `requirePermission(action)` middleware
 * call site, so the type system catches typos at compile time. */
export type Action = {
  [R in Resource]: `${R & string}.${keyof (typeof PERMISSIONS)[R] & string}`
}[Resource]

/** Look up the allowed roles for a `(resource.action)` key. Pure
 * function — call from middleware, call from the frontend hook,
 * call from a test. Same answer everywhere. */
export function actionRoles(action: Action): ReadonlyArray<Role> {
  const [resource, op] = action.split('.') as [Resource, string]
  const resourceMap = PERMISSIONS[resource] as Record<string, ReadonlyArray<Role>>
  const roles = resourceMap[op]
  if (!roles) {
    // Unknown action key — fail closed. The compile-time `Action`
    // type should make this unreachable, but a runtime check avoids
    // a silent allow-all on a mistyped key from non-TS callers.
    throw new Error(`[permissions] unknown action: ${action}`)
  }
  return roles
}

/** True iff `role` is allowed to perform `action`. The single
 * predicate every gate (server middleware + frontend hook) calls. */
export function hasPermission(role: Role | undefined | null, action: Action): boolean {
  if (!role) return false
  return actionRoles(action).includes(role)
}

// ─────────────────────────────────────────────────────────────────────
// Workspace-tier permissions (#526 PR 2)
// ─────────────────────────────────────────────────────────────────────
//
// The legacy `PERMISSIONS` matrix above is keyed on `Role` (org tier).
// Under ADR 0010 the access model becomes two-tier: governance at the
// org and operations at the workspace. The workspace-tier permissions
// live in their own matrix keyed on `WorkspaceRole` so the two tiers
// don't share a role union (each tier answers a different question
// and shoehorning into one shared union was the failure mode of
// GitLab-style designs).
//
// PR 2 ships this as ADDITIVE — the legacy `Role` and `PERMISSIONS`
// stay intact for routes that haven't been flipped yet, and PR 3
// reshapes the org tier itself (`tester` and `reviewer` drop off
// `OrgRole`; `billing` and `member` come on). When that lands, the
// workspace-scoped resources that still appear under the legacy
// `PERMISSIONS` (today: workspaces.read/update/delete) are removed
// from there — they live ONLY in WORKSPACE_PERMISSIONS post-PR-3.

/** Closed set of workspace-tier roles. Distinct from the org-tier
 * `Role` union above — operational tier answers "what can this
 * subject do inside this workspace" whereas governance tier answers
 * "what can this user do at the org level". See ADR 0010 for the
 * rationale and the rejected alternatives.
 *
 *   admin    — workspace settings + member management + all tester powers
 *   tester   — author / edit / delete tests, runs (no settings, no
 *              workspace member changes)
 *   reviewer — read-only + comment
 *
 * No `owner` role — that word is reserved for the org tier so the
 * system can talk about "owner" without disambiguation. The most-
 * permissive workspace role is `admin`. */
export type WorkspaceRole = 'admin' | 'tester' | 'reviewer'

/** Typed handle for workspace roles (same shape as `Role` above). */
export const WorkspaceRole = {
  admin: 'admin',
  tester: 'tester',
  reviewer: 'reviewer',
} as const satisfies Record<WorkspaceRole, WorkspaceRole>

/** All three workspace roles in permissiveness order. */
export const ALL_WORKSPACE_ROLES: ReadonlyArray<WorkspaceRole> = [
  WorkspaceRole.admin,
  WorkspaceRole.tester,
  WorkspaceRole.reviewer,
]

/** Workspace-tier permissions matrix. Keyed on `WorkspaceRole`.
 * Mirrors the workspace-scoped resources from the legacy
 * `PERMISSIONS` matrix but with the new role union. Adds the
 * `workspaceMembers.*` namespace (manage endpoints from PR 1) and
 * `workspaces.read|update|delete` (the per-workspace lifecycle ops
 * that aren't governance — workspace `Admin` controls them, with
 * org Owner reaching via the implicit-Admin fallback at the
 * middleware seam).
 *
 * `workspaces.create` stays in the org-tier matrix above —
 * creating a new workspace is a governance action ("stand up a
 * workspace in this org"), not an operational one. */
export const WORKSPACE_PERMISSIONS = {
  // Per-workspace lifecycle (post-creation) — Admin controls these
  // operationally; org Owner reaches via implicit Admin.
  workspaces: {
    read:   ALL_WORKSPACE_ROLES,
    update: [WorkspaceRole.admin],
    delete: [WorkspaceRole.admin],
  },

  // Workspace-member management — the routes shipped in PR 1. Today
  // the route gate is inlined; PR 2 routes them through the matrix.
  workspaceMembers: {
    list:   [WorkspaceRole.admin],
    grant:  [WorkspaceRole.admin],
    update: [WorkspaceRole.admin],
    revoke: [WorkspaceRole.admin],
  },

  // Test artifacts (workspace-scoped) — Admin and Tester author /
  // edit / delete; Reviewer is read-only.
  applications:    { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  variants:        { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  testCases:       { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  testPlans:       { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  testScripts:     { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  testSuites:      { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  testRuns:        {
    read:               ALL_WORKSPACE_ROLES,
    write:              [WorkspaceRole.admin, WorkspaceRole.tester],
    // Phase B (#642) reporter scope — PAT-bearer authorization for the
    // forthcoming GitHub Check Run / GitLab status / Azure DevOps build
    // status reporters that post results back to the customer's CI
    // surface. Added to the catalog now alongside `ciIntegrations.write`
    // even though A1 (this PR) doesn't exercise it yet, so the catalog
    // edit lands in one PR through `@ottotester/permissions` rather
    // than two.
    reportIntegration:  [WorkspaceRole.admin],
  },
  hints:           { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  replayTemplates: { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },
  scheduledRuns:   { read: ALL_WORKSPACE_ROLES, write: [WorkspaceRole.admin, WorkspaceRole.tester] },

  // CI/CD integration records — workspace-scoped credentials governing
  // how ottotester reports run results back to the customer's CI
  // (webhook receiver, GitHub App / GitLab connection / Azure DevOps
  // service connection). Admin-only because the records carry an
  // HMAC secret + OAuth-style provider tokens whose visibility is a
  // trust-boundary concern. See ADR 0015 § referential integrity +
  // outbound reporter shape; #642 Phase A1 onward.
  ciIntegrations:  { read: [WorkspaceRole.admin], write: [WorkspaceRole.admin] },

  // Inbound issue-tracker connections (Jira, ...) — the workspace-level
  // credential store an app binds to for requirement/defect coverage
  // (#1558 Slice 1a). Admin-only, same trust-boundary reasoning as
  // ciIntegrations: connecting/rotating stores a provider API token whose
  // visibility is workspace-wide. Binding an app to a connection is a
  // separate, less-privileged action that lands in Slice 1b.
  issueTrackers:   { read: [WorkspaceRole.admin], write: [WorkspaceRole.admin] },

  // Credentials (workspace-scoped; structure visible to all, bearer
  // values redacted for reviewer). Same special-casing as the legacy
  // matrix.
  credentials: {
    read:          ALL_WORKSPACE_ROLES,
    readPlaintext: [WorkspaceRole.admin, WorkspaceRole.tester],
    write:         [WorkspaceRole.admin, WorkspaceRole.tester],
  },
  testData: {
    write: [WorkspaceRole.admin, WorkspaceRole.tester],
  },

  // Workspace-tier read of global fixtures. Any signed-in member of
  // a workspace can see system fixtures (they're a shared catalog,
  // not a per-workspace concern — but route-side enforcement is
  // workspace-scoped so the matrix entry lives here).
  systemFixtures: {
    read: ALL_WORKSPACE_ROLES,
  },

  // BI / Analytics — workspace-scoped data plane. Tightened beyond
  // the legacy matrix's "owner only" because workspace `Admin` is
  // the workspace's governance tier and should see its own
  // analytics. Cross-workspace / cross-org analytics still gate on
  // org tier at the route layer.
  bi: {
    read: [WorkspaceRole.admin],
  },
} as const satisfies Record<string, Record<string, ReadonlyArray<WorkspaceRole>>>

/** Workspace resource keys (top level of the workspace matrix). */
export type WorkspaceResource = keyof typeof WORKSPACE_PERMISSIONS

/** All workspace `(resource.action)` keys, dot-joined. Drives the
 * typed accessor `actionRolesWorkspace()` and the
 * `requireWorkspacePermission(action)` middleware. */
export type WorkspaceAction = {
  [R in WorkspaceResource]: `${R & string}.${keyof (typeof WORKSPACE_PERMISSIONS)[R] & string}`
}[WorkspaceResource]

/** Look up the allowed workspace-tier roles for a workspace
 * `(resource.action)` key. */
export function actionRolesWorkspace(action: WorkspaceAction): ReadonlyArray<WorkspaceRole> {
  const [resource, op] = action.split('.') as [WorkspaceResource, string]
  const resourceMap = WORKSPACE_PERMISSIONS[resource] as Record<string, ReadonlyArray<WorkspaceRole>>
  const roles = resourceMap[op]
  if (!roles) {
    throw new Error(`[permissions] unknown workspace action: ${action}`)
  }
  return roles
}

/** True iff `workspaceRole` is allowed to perform `action` at the
 * workspace tier. The companion predicate to `hasPermission` for the
 * org tier. */
export function hasWorkspacePermission(
  workspaceRole: WorkspaceRole | undefined | null,
  action: WorkspaceAction,
): boolean {
  if (!workspaceRole) return false
  return actionRolesWorkspace(action).includes(workspaceRole)
}
